import { type Editor } from '@tiptap/react';
import { type MutableRefObject, useEffect, useRef } from 'react';

import { type WorkspaceAgentTaskSubmittedType } from '@air/analytics';

import { applyWorkspaceAgentMarketingComposerDraft } from '~/components/v2/components/workspace/workspace-agent-panel/utils/applyWorkspaceAgentMarketingComposerDraft';
import {
  isWorkspaceAgentPanelExpanded,
  useWorkspaceAgentPanelStore,
} from '~/components/v2/store/features/workspace/useWorkspaceAgentPanelStore';

type UsePrivateWorkspaceAgentMarketingComposerDraftSyncParams = {
  editor: Editor | null;
  isCreditsDepleted: boolean;
  taskSubmitTypeRef: MutableRefObject<WorkspaceAgentTaskSubmittedType>;
  workspaceId: string;
};

const composerMatchesMarketingDraftPrompt = (editor: Editor, prompt: string): boolean =>
  editor.getText().trim() === prompt.trim();

/**
 * Applies a marketing `agentUseCase` draft from the panel store whenever the mounted TipTap editor
 * does not yet hold the prompt. The draft stays in the panel store after apply so a remounted empty
 * editor can be filled again; it is cleared on send, user edits, credits depletion, or session reset.
 */
export const usePrivateWorkspaceAgentMarketingComposerDraftSync = ({
  editor,
  isCreditsDepleted,
  taskSubmitTypeRef,
  workspaceId,
}: UsePrivateWorkspaceAgentMarketingComposerDraftSyncParams) => {
  const marketingComposerDraft = useWorkspaceAgentPanelStore((state) => state.marketingComposerDraft);
  const clearMarketingComposerDraft = useWorkspaceAgentPanelStore((state) => state.clearMarketingComposerDraft);
  const isPanelExpanded = useWorkspaceAgentPanelStore((state) => isWorkspaceAgentPanelExpanded({ dock: state.dock }));
  const marketingComposerDraftRef = useRef(marketingComposerDraft);

  marketingComposerDraftRef.current = marketingComposerDraft;

  useEffect(() => {
    if (!editor || editor.isDestroyed || !marketingComposerDraft || !isPanelExpanded) {
      return;
    }

    if (marketingComposerDraft.workspaceId !== workspaceId) {
      return;
    }

    if (isCreditsDepleted) {
      clearMarketingComposerDraft();

      return;
    }

    const didApply = applyWorkspaceAgentMarketingComposerDraft({
      editor,
      prompt: marketingComposerDraft.prompt,
    });

    if (didApply) {
      taskSubmitTypeRef.current = 'marketing_url';
    }
  }, [
    clearMarketingComposerDraft,
    editor,
    isCreditsDepleted,
    isPanelExpanded,
    marketingComposerDraft,
    taskSubmitTypeRef,
    workspaceId,
  ]);

  useEffect(() => {
    if (!editor || editor.isDestroyed) {
      return;
    }

    const handleUpdate = ({ transaction }: { transaction: { docChanged: boolean } }) => {
      const draft = marketingComposerDraftRef.current;

      if (!draft || !transaction.docChanged || draft.workspaceId !== workspaceId) {
        return;
      }

      if (composerMatchesMarketingDraftPrompt(editor, draft.prompt)) {
        return;
      }

      clearMarketingComposerDraft();
    };

    editor.on('update', handleUpdate);

    return () => {
      editor.off('update', handleUpdate);
    };
  }, [clearMarketingComposerDraft, editor, workspaceId]);
};
